import { useEffect, useState } from "react";
import { api } from "../../api";
import { toast } from "sonner";
import { Mail, Send, ExternalLink, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

export default function EmailSettings() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.emailStatus().then(setStatus).catch(() => setStatus({ error: true })); }, []);

  const sendNow = async () => {
    setSending(true);
    try { await api.digestSendNow(); toast.success("Digest sent to all admins"); }
    catch (e) { toast.error(String(e.message || e)); }
    setSending(false);
  };

  const dnsRecords = [
    { type: "MX",  name: "send.mail.yourseventfully.com", value: "feedback-smtp.us-east-1.amazonses.com (priority 10)", note: "Sending" },
    { type: "TXT", name: "send.mail.yourseventfully.com", value: "v=spf1 include:amazonses.com ~all",                    note: "SPF" },
    { type: "TXT", name: "resend._domainkey.mail.yourseventfully.com", value: "(long p= value shown by Resend after add)", note: "DKIM" },
    { type: "TXT", name: "_dmarc.mail.yourseventfully.com", value: "v=DMARC1; p=none;",                                    note: "DMARC (optional)" },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>System · Admin only</div>
        <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Email &amp; Digest</h1>
      </div>

      {!status ? <div className="muted">Loading…</div> : status.error ? <div className="muted">Failed to load.</div> : (
        <>
          <div className="panel" style={{ padding: 22, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div className="muted" style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Current sender</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 6, fontFamily: "Space Grotesk" }} data-testid="email-from">{status.from}</div>
                {status.reply_to && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Replies go to <span style={{ color: "var(--mint)" }}>{status.reply_to}</span></div>}
              </div>
              {status.sandbox ? (
                <span className="badge badge-warn" data-testid="sandbox-badge" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={11} /> Sandbox mode
                </span>
              ) : (
                <span className="badge badge-mint" data-testid="live-badge" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <CheckCircle2 size={11} /> Live
                </span>
              )}
            </div>
            {status.sandbox && (
              <p className="muted" style={{ fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                You're on Resend's shared sandbox address. Invites and the weekly digest can only be delivered to the email tied to your Resend account — not to the wider team. Verify a domain below to unlock real delivery for everyone.
              </p>
            )}
          </div>

          <div className="panel" style={{ padding: 22, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Verify a sending domain</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Recommended subdomain: <code style={{ color: "var(--mint)" }}>mail.yourseventfully.com</code></div>
              </div>
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="btn"><ExternalLink size={13} /> Open Resend Domains</a>
            </div>
            <ol className="muted" style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 20, marginTop: 0, marginBottom: 18 }}>
              <li>Sign in to Resend and click <strong style={{ color: "var(--text)" }}>Add Domain</strong>.</li>
              <li>Enter <code style={{ color: "var(--mint)" }}>mail.yourseventfully.com</code> and pick a region (US East is fine).</li>
              <li>Resend will show you 3–4 DNS records. Add them at your DNS provider (Cloudflare / GoDaddy / Squarespace / wherever <code>yourseventfully.com</code> lives).</li>
              <li>Come back to Resend and click <strong style={{ color: "var(--text)" }}>Verify</strong>. It usually completes within a few minutes.</li>
              <li>Ping me (or edit <code>RESEND_FROM</code> in backend <code>.env</code>) to switch to <code style={{ color: "var(--mint)" }}>YE Music &lt;team@mail.yourseventfully.com&gt;</code> and this badge flips to Live.</li>
            </ol>
            <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Records you'll paste (Resend will show the exact values)</div>
            <div style={{ overflowX: "auto", background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <table>
                <thead><tr><th>Type</th><th>Name / Host</th><th>Value</th><th>Purpose</th></tr></thead>
                <tbody>
                  {dnsRecords.map((r, i) => (
                    <tr key={i}>
                      <td><span className="badge badge-mint">{r.type}</span></td>
                      <td><code style={{ fontSize: 11 }}>{r.name}</code></td>
                      <td><code style={{ fontSize: 11 }}>{r.value}</code></td>
                      <td className="muted" style={{ fontSize: 12 }}>{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel" style={{ padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Weekly Monday briefing</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Auto-sent every Monday at 9:00 AM IST to all Admins with a snapshot of releases in motion, pending approvals, and net position.</div>
              </div>
              <button className="btn btn-primary" onClick={sendNow} disabled={sending} data-testid="digest-send-now">
                <Send size={13} /> {sending ? "Sending…" : "Send now (preview)"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
